/**
 * **A download the page can watch — RD-17.**
 *
 * Abi, 15 Sep: *"it takes a while to download the entire snap raw data — can we
 * have some sort of indicator that it's working?"*
 *
 * Exports were plain `<a href>` navigations. The browser goes off and streams,
 * and **nothing in the app changes state** — on the six-figure-row Snapchat
 * datasets that is several seconds of a page that looks broken, with no way to
 * tell a slow export from a dead click. Routing it through `fetch` is what lets
 * the button report.
 *
 * ### Bytes, never a percentage
 *
 * `/api/raw-data/export` is a `StreamingResponse` of `text/csv`, so the response
 * is chunked and carries **no `Content-Length`**. That is deliberate on the
 * server — the row count is not known until the query is walked — and it means a
 * progress *bar* is not available at any price. Bytes received is the honest
 * indicator, and a spinner plus a growing number is a truthful one: it says
 * *something is arriving*, which is exactly the question being asked.
 *
 * ### Why not keep the anchor and just show a spinner
 *
 * Because there is no event to turn it off. A navigation download fires nothing
 * back into the page, so the spinner would be a timer pretending to be progress.
 *
 * ### What this also fixes
 *
 * A denied dataset returned `403` **as a downloaded file**. `RD-13`'s rule is
 * that an export refuses by name rather than handing back an empty CSV — true on
 * the server, and then undone by the browser writing the refusal to disk as
 * `export.csv`. Through `fetch` the error is a value the page can render.
 */

/** Progress callback: total bytes received so far. */
export type OnProgress = (bytesReceived: number) => void

export interface DownloadResult {
  bytes: number
}

/**
 * Read the `filename` out of `Content-Disposition`, falling back to a sensible
 * name.
 *
 * The server names these files and the name carries the dataset and the range,
 * so losing it would make a folder of exports unidentifiable. Handles both
 * `filename="x.csv"` and RFC 5987 `filename*=UTF-8''x.csv`.
 */
export function filenameFrom(header: string | null, fallback: string): string {
  if (!header) return fallback
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1])
    } catch {
      // A malformed header is not worth failing a download over.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain?.[1] ?? fallback
}

/**
 * Fetch `url` and save it, reporting bytes as they arrive.
 *
 * Throws on a non-2xx so the caller can render the reason; the body of an error
 * response is read as text, because that is where the API puts its `detail`.
 */
export async function downloadWithProgress(
  url: string,
  fallbackName: string,
  onProgress?: OnProgress,
): Promise<DownloadResult> {
  // Same origin, so the session cookie rides along — `SameSite=Strict` is only
  // possible because the API and the SPA share an origin (ARCHITECTURE.md §7).
  const response = await fetch(url, { credentials: 'same-origin' })

  if (!response.ok) {
    let detail = `Export failed (${response.status})`
    try {
      const body = await response.json()
      if (typeof body?.detail === 'string') detail = body.detail
    } catch {
      // Not JSON. The status is the answer.
    }
    throw new Error(detail)
  }

  const name = filenameFrom(response.headers.get('content-disposition'), fallbackName)

  // `response.body` is absent in jsdom and in older Safari. Falling back to
  // `blob()` loses the progress read-out but never the download itself — an
  // export that works without a counter beats a counter that breaks the export.
  let blob: Blob
  if (response.body) {
    const reader = response.body.getReader()
    const chunks: Uint8Array[] = []
    let received = 0
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      if (value) {
        chunks.push(value)
        received += value.length
        onProgress?.(received)
      }
    }
    blob = new Blob(chunks as BlobPart[], {
      type: response.headers.get('content-type') ?? 'application/octet-stream',
    })
  } else {
    blob = await response.blob()
    onProgress?.(blob.size)
  }

  const href = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = href
    anchor.download = name
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    // Revoked on a later tick: Safari has been observed to cancel a download
    // whose object URL is revoked in the same frame as the click.
    setTimeout(() => URL.revokeObjectURL(href), 10_000)
  }

  return { bytes: blob.size }
}

/** `1.2 MB`, `840 KB`, `512 B` — a size a person reads, not a byte count. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
